import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, ArrowRight, ArrowLeft, Database, FileSpreadsheet, Table2, Plug,
  Sparkles, CheckCircle2, AlertTriangle, Clock, Users2, DoorOpen, Play, X,
} from "@/components/icons";
import { useStore } from "@/store/store";
import { IliaMark, IliaAvatar, Chip, SourceTag } from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";

const ERPS = [
  { id: "Medesk", descKey: "admin.setup.erp.clinicManagement", Icon: Database },
  { id: "Bitrix24", descKey: "admin.setup.erp.crmBookings", Icon: Plug },
  { id: "1C:Enterprise", descKey: "admin.setup.erp.accounting", Icon: Database },
  { id: "ClinicSoft", descKey: "admin.setup.erp.patientRecords", Icon: Table2 },
  { id: "Google Sheets", descKey: "admin.setup.erp.sharedPriceList", Icon: FileSpreadsheet },
  { id: "CSV / Excel", descKey: "admin.setup.erp.uploadFile", Icon: FileSpreadsheet },
];

const STEPS = ["source", "import", "confirm", "availability", "done"];

const HANDOFF_RULES = [
  { id: "money", labelKey: "admin.setup.rule.money", hintKey: "admin.setup.rule.recommended" },
  { id: "lowconf", labelKey: "admin.setup.rule.lowConfidence", hintKey: "admin.setup.rule.lowConfidenceHint" },
  { id: "request", labelKey: "admin.setup.rule.customerRequests", hintKey: "admin.setup.rule.mostAutonomous" },
];

export default function Setup() {
  const { state, actions } = useStore();
  const { t, localize } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [erp, setErp] = useState(null);
  const [progress, setProgress] = useState(0);
  const [scanned, setScanned] = useState(false);

  const [thyroidPrice, setThyroidPrice] = useState("");
  const [stressPrice, setStressPrice] = useState("");
  const [stressDur, setStressDur] = useState("");
  const [rule, setRule] = useState("money");

  // scan animation on entering step 1
  useEffect(() => {
    if (step !== 1) return;
    setProgress(0); setScanned(false);
    const started = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(100, Math.round(((Date.now() - started) / 1900) * 100));
      setProgress(p);
      if (p >= 100) { clearInterval(iv); setScanned(true); }
    }, 90);
    return () => clearInterval(iv);
  }, [step]);

  const cleanServices = state.services.filter((s) => s.source !== "review");
  const thyroidResolved = !state.services.some((s) => s.id === "s-thyroid" && s.source === "review");
  const stressExists = state.services.some((s) => s.id === "s-stress");

  const connect = () => { actions.setErp(erp); setStep(1); };

  const applyGaps = () => {
    if (thyroidPrice && !thyroidResolved) actions.updateService("s-thyroid", { price: Number(thyroidPrice), source: "imported" });
    if (stressPrice && stressDur && !stressExists) {
      actions.addService({ id: "s-stress", name: "Stress ECG test", duration: Number(stressDur), price: Number(stressPrice), resource: "r-nino", room: "r-ecg", buffer: 15, source: "imported" });
    }
    actions.completeActivation("act-2");
    actions.completeActivation("act-3");
    setStep(3);
  };

  const finishAvailability = () => {
    actions.completeActivation("act-4");
    actions.completeActivation("act-5");
    setStep(4);
  };

  const gapsRemaining = (thyroidResolved ? 0 : 1) + (stressExists ? 0 : 1);

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* left rail */}
      <div className="hidden w-72 shrink-0 flex-col justify-between border-r border-border bg-surface p-7 lg:flex">
        <div>
          <div className="mb-10 flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white shadow-xs"><IliaMark size={26} /></span>
            <span className="text-[15px] font-semibold tracking-tight">Askilia</span>
          </div>
          <ol className="space-y-1">
            {STEPS.map((s, i) => (
              <li key={s} className={cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm", i === step ? "bg-primary-soft text-primary-soft-foreground" : "text-muted-foreground")}>
                <span className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold", i < step ? "bg-success text-success-foreground" : i === step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className={i === step ? "font-medium" : ""}>{t(`admin.setup.step.${s}`)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-xl border border-border bg-ilia-soft/50 p-4">
          <div className="mb-2 flex items-center gap-2"><IliaAvatar size={26} active /><span className="text-xs font-semibold">Ilia</span></div>
          <p className="text-xs leading-relaxed text-ilia-soft-foreground">{t("admin.setup.iliaHelp")}</p>
        </div>
      </div>

      {/* content */}
      <div className="relative flex flex-1 flex-col rosehip-glow">
        <div className="flex items-center justify-between gap-3 px-5 py-4 lg:px-10">
          <p className="text-xs font-medium text-muted-foreground lg:hidden">{t("admin.setup.mobileStep", { current: step + 1, total: STEPS.length, label: t(`admin.setup.step.${STEPS[step]}`) })}</p>
          <span className="hidden lg:block" />
          <div className="flex shrink-0 items-center gap-2">
            <div className="inline-flex items-center rounded-lg border border-border bg-surface p-0.5 shadow-xs" role="group" aria-label={t("language.title")}>
              {[
                { id: "en", label: "EN" },
                { id: "ka", label: "KA" },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={state.lang === option.id}
                  aria-label={option.id === "en" ? t("language.english") : t("language.georgian")}
                  onClick={() => actions.setLang(option.id)}
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    state.lang === option.id ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav("/today")} aria-label={t("admin.setup.skipWorkspace")}>
              <span className="hidden sm:inline">{t("admin.setup.skipWorkspace")}</span><X className="h-4 w-4 sm:ml-1.5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 items-start justify-center overflow-y-auto scroll-thin px-5 pb-16 lg:px-10">
          <div className="w-full max-w-2xl py-4">

            {/* STEP 0 — source */}
            {step === 0 && (
              <div className="animate-fade-rise">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("admin.setup.connectTitle")}</h1>
                <p className="mt-1.5 text-sm text-muted-foreground">{t("admin.setup.connectDescription")}</p>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {ERPS.map((e) => (
                    <button key={e.id} onClick={() => setErp(e.id)}
                      className={cn("flex items-center gap-3 rounded-xl border p-4 text-left transition-colors", erp === e.id ? "border-primary bg-primary-soft/50 ring-1 ring-primary" : "border-border bg-surface hover:border-ring/40")}>
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground"><e.Icon className="h-5 w-5" /></span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground">{e.id}</span>
                        <span className="block text-xs text-muted-foreground">{t(e.descKey)}</span>
                      </span>
                      {erp === e.id && <Check className="ml-auto h-4 w-4 text-primary" />}
                    </button>
                  ))}
                </div>
                <div className="mt-7 flex justify-end">
                  <Button disabled={!erp} onClick={connect}>{erp ? t("admin.setup.connectNamed", { name: erp }) : t("admin.setup.connectSource")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* STEP 1 — importing */}
            {step === 1 && (
              <div className="animate-fade-rise">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">{scanned ? t("admin.setup.foundTitle") : t("admin.setup.readingTitle", { name: erp })}</h1>
                <p className="mt-1.5 text-sm text-muted-foreground">{scanned ? t("admin.setup.foundDescription") : t("admin.setup.readingDescription")}</p>

                {!scanned ? (
                  <div className="mt-8 rounded-xl border border-border bg-surface p-6 shadow-xs">
                    <div className="mb-4 flex items-center gap-3">
                      <IliaAvatar size={34} active />
                      <p className="text-sm font-medium text-foreground">{t("admin.setup.readingData", { name: erp })}</p>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <p className="mt-2 font-sans text-xs font-medium text-muted-foreground tabular-nums">{progress}% · {progress < 40 ? t("admin.setup.progress.services") : progress < 70 ? t("admin.setup.progress.resources") : progress < 95 ? t("admin.setup.progress.hours") : t("admin.setup.progress.gaps")}</p>
                  </div>
                ) : (
                  <>
                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <Discovered icon={Table2} n={state.services.length} labelKey="admin.setup.discovered.services" />
                      <Discovered icon={Users2} n={state.resources.filter((r) => r.type === "person").length} labelKey="admin.setup.discovered.practitioners" />
                      <Discovered icon={DoorOpen} n={state.resources.filter((r) => r.type === "room").length} labelKey="admin.setup.discovered.rooms" />
                      <Discovered icon={Clock} n="10–19" labelKey="admin.setup.discovered.days" />
                    </div>
                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-warning/40 bg-warning-soft p-3.5 text-sm text-warning-soft-foreground">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span><strong>{t("admin.setup.confirmTwo")}</strong> {t("admin.setup.importedCleanly")}</span>
                    </div>
                    <div className="mt-7 flex justify-end">
                      <Button onClick={() => setStep(2)}>{t("admin.setup.reviewGaps", { count: 2 })} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* STEP 2 — confirm gaps */}
            {step === 2 && (
              <div className="animate-fade-rise">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("admin.setup.gapsTitle")}</h1>
                <p className="mt-1.5 text-sm text-muted-foreground">{t("admin.setup.gapsDescription", { count: gapsRemaining > 0 ? gapsRemaining : t("admin.setup.few") })}</p>

                {/* gap 1 — thyroid price */}
                <GapCard
                  title={localize("Thyroid ultrasound")}
                  reason={t("admin.setup.thyroidReason")}
                  done={thyroidResolved}
                >
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("admin.setup.priceLabel")}</label>
                      <Input inputMode="numeric" value={thyroidPrice} onChange={(e) => setThyroidPrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("admin.setup.pricePlaceholder", { value: 75 })} className="bg-background" disabled={thyroidResolved} />
                    </div>
                    <Chip tone="muted">{t("admin.setup.durationDoctor", { minutes: 25, doctor: localize("Dr. Lika") })}</Chip>
                  </div>
                </GapCard>

                {/* gap 2 — new stress ECG */}
                <GapCard
                  title={t("admin.setup.stressEcg")}
                  reason={t("admin.setup.stressReason")}
                  done={stressExists}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("admin.setup.priceLabel")}</label>
                      <Input inputMode="numeric" value={stressPrice} onChange={(e) => setStressPrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("admin.setup.pricePlaceholder", { value: 90 })} className="bg-background" disabled={stressExists} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("admin.setup.durationLabel")}</label>
                      <Input inputMode="numeric" value={stressDur} onChange={(e) => setStressDur(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("admin.setup.durationPlaceholder", { value: 45 })} className="bg-background" disabled={stressExists} />
                    </div>
                  </div>
                </GapCard>

                {/* clean import summary */}
                <div className="mt-4 rounded-xl border border-border bg-surface p-4 shadow-xs">
                  <p className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground"><CheckCircle2 className="h-4 w-4 text-success" /> {t("admin.setup.noActionNeeded")}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cleanServices.slice(0, 8).map((s) => <Chip key={s.id} tone="success">{localize(s.name)}</Chip>)}
                  </div>
                </div>

                <div className="mt-7 flex items-center justify-between">
                  <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("admin.back")}</Button>
                  <Button onClick={applyGaps}>{t("admin.saveContinue")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* STEP 3 — availability + rule */}
            {step === 3 && (
              <div className="animate-fade-rise">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("admin.setup.availabilityTitle")}</h1>
                <p className="mt-1.5 text-sm text-muted-foreground">{t("admin.setup.availabilityDescription")}</p>

                <div className="mt-6 rounded-xl border border-border bg-surface p-4 shadow-xs">
                  <p className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground"><Clock className="h-4 w-4 text-primary" /> {t("admin.setup.openingHours")} <SourceTag source="imported" /></p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                      <div key={d} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm">
                        <span className="text-muted-foreground">{t(`admin.setup.day.${d.toLowerCase()}`)}</span>
                        <span className="font-sans font-medium text-foreground tabular-nums">10:00–19:00</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-border bg-surface p-4 shadow-xs">
                  <p className="mb-1 text-sm font-medium text-foreground">{t("admin.setup.handoffTitle")}</p>
                  <p className="mb-3 text-xs text-muted-foreground">{t("admin.setup.handoffDescription")}</p>
                  <div className="space-y-2">
                    {HANDOFF_RULES.map((r) => (
                      <button key={r.id} onClick={() => setRule(r.id)}
                        className={cn("flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors", rule === r.id ? "border-primary bg-primary-soft/50 ring-1 ring-primary" : "border-border bg-background hover:border-ring/40")}>
                        <span className={cn("inline-flex h-4 w-4 items-center justify-center rounded-full border", rule === r.id ? "border-primary bg-primary" : "border-muted-foreground/40")}>
                          {rule === r.id && <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
                        </span>
                        <span className="flex-1 font-medium text-foreground">{t(r.labelKey)}</span>
                        <Chip tone={r.id === "money" ? "success" : "muted"}>{t(r.hintKey)}</Chip>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-7 flex items-center justify-between">
                  <Button variant="ghost" onClick={() => setStep(2)}><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("admin.back")}</Button>
                  <Button onClick={finishAvailability}>{t("admin.setup.looksRight")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* STEP 4 — done */}
            {step === 4 && (
              <div className="animate-fade-rise text-center">
                <span className="mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success"><CheckCircle2 className="h-8 w-8" /></span>
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("admin.setup.importedTitle", { name: erp })}</h1>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  {t("admin.setup.doneDescription")}
                </p>
                <div className="mt-7 flex flex-col items-center justify-center gap-2 sm:flex-row">
                  <Button onClick={() => { toast.success(t("admin.setup.testPassed"), { description: t("admin.setup.testPassedDescription") }); actions.completeActivation("act-7"); nav("/settings/activation"); }}>
                    <Play className="mr-1.5 h-4 w-4" /> {t("admin.setup.runTest")}
                  </Button>
                  <Button variant="outline" onClick={() => nav("/today")}>{t("admin.setup.goWorkspace")}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Discovered({ icon: Icon, n, labelKey }) {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-border bg-surface p-3.5 text-center shadow-xs">
      <Icon className="mx-auto mb-1.5 h-5 w-5 text-primary" />
      <p className="text-lg font-semibold text-foreground">{n}</p>
      <p className="text-[11px] text-muted-foreground">{t(labelKey)}</p>
    </div>
  );
}

function GapCard({ title, reason, done, children }) {
  const { t } = useI18n();
  return (
    <div className={cn("mt-4 rounded-xl border p-4 shadow-xs transition-colors", done ? "border-success/40 bg-success-soft/50" : "border-warning/40 bg-surface")}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">{title} {done && <Chip tone="success"><Check className="h-3 w-3" /> {t("admin.confirmed")}</Chip>}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{reason}</p>
        </div>
        {!done && <SourceTag source="review" />}
      </div>
      {!done && children}
    </div>
  );
}
